<script lang="ts">
  import type { Options } from '@splidejs/splide';
  import { Splide, SplideSlide } from '@splidejs/svelte-splide';
  import { onMount } from 'svelte';
  import { getAppConfig } from '../../../../../common/config/app-config';
  import {
    applyRTLArrowCss,
    getProductDataForGTM,
    isDirectionRTL,
    isPointWithinElementBounds,
    onClickWishlist,
  } from '../../../../../common/util';
  import ProductTileMain from '../../../../common/components/productTile/ProductTileMain.svelte';
  import viewport from '../../../../common/components/useViewportAction';
  import RecommendationTileIndex from './RecommendationTileIndex.svelte';
  import ProductGridTile from '../../../../product-listing-search/components/productGridTile.svelte';

  export let recommendationType = 1;
  export let tileData = [];
  export let showTileIndex = true;

  onMount(() => {
    const parent = document.querySelectorAll('.product-tile-index-container');
    applyRTLArrowCss(parent, 'auto', '1em', '1em', 'auto');
  });

  const { encodedContextPath = 'en-sa', pageId } = getAppConfig();

  function handleRRItemClick(event, rrClickTrackingURL, _href, tile) {
    event.preventDefault();
    // check if wishlist icon clicked
    const target = event.target as Element | null;

    const wishlistWrapper = target?.closest('.product-tile-container')?.querySelector('.fav-wrapper') ?? null;
    if (wishlistWrapper) {
      if (isPointWithinElementBounds(event, wishlistWrapper)) {
        // do nothing
        return false;
      }
    }

    if (navigator.sendBeacon && rrClickTrackingURL) {
      navigator.sendBeacon(rrClickTrackingURL);
    }
    if (tile !== undefined) {
      window['ACC'].extragtm.click.trackRecommendationsClick(tile);
      window['ACC'].mixpanel.trackRecommendationsClick(tile);
    }
    if (tile?.isRecommendation) {
      localStorage.setItem('recommendationClicked', 'true');
    }
    window.location.assign(_href);
  }

  const isRTL = isDirectionRTL();

  const thumbsOptions: Options = {
    type: 'slide',
    arrows: true,
    rewind: false,
    pagination: false,
    perPage: 2,
    perMove: 1,
    autoWidth: true,
    autoHeight: true,
    releaseWheel: true,
    cover: true,
    updateOnMove: true,
    direction: (isRTL && 'rtl') || 'ltr',
    breakpoints: {
      900: {
        arrows: false,
        width: '92vw',
        perPage: 1,
      },
    },
    classes: {
      arrow: 'splide__arrow splide_arrow-bg arrows-bg',
      prev: 'splide__arrow--prev splide_arrow-prev',
      next: 'splide__arrow--next splide_arrow-next',
    },
  };
</script>

{#if tileData.length}
  <div
    class="product-tile-index-container rr_parent_item_container"
    class:smallprodtile={showTileIndex}
    use:viewport
    on:enterViewport={() => window['ACC'].extragtm.click.trackViewList('', '', tileData)}
  >
    <Splide
      options={{
        ...thumbsOptions,
      }}
    >
      {#each tileData as tile, index}
        <SplideSlide>
          <div class="tile-index-wrapper d-flex position-relative">
            {#if showTileIndex}
              <div class="tile-index">
                <RecommendationTileIndex index={index + 1} {recommendationType} />
              </div>
            {/if}
            <div class="main-tile {showTileIndex ? 'index-margin' : ''}" class:rtl={isRTL}>
              <a
                class="recomendation-viewed-link {pageId ? 'pdp-card-link' : ''}"
                href={`${encodedContextPath}${tile.url ?? tile.urlEn}`}
                data-product-id={tile.productCode}
                on:click|preventDefault|stopPropagation={e =>
                  handleRRItemClick(e, tile?.rrClickUrl, `${encodedContextPath}${tile.url ?? tile.urlEn}`, tile)}
              >
                {#if pageId === 'productDetails'}
                  <ProductGridTile
                    product={tile}
                    productCardViewType="grid-view"
                    productIndex={index}
                    isComparedChecked={false}
                    options={{ mobileTileWidth: 166, desktopTileWidth: 238 }}
                  />
                {:else}
                  <ProductTileMain
                    data={tile}
                    options={{ isRRComponent: true, showActionBtn: false }}
                    on:toggleFav={data => {
                      onClickWishlist(data, tile.code || tile.productCode);
                    }}
                    productDataForGTM={getProductDataForGTM(tile, index)}
                  />
                {/if}
              </a>
            </div>
          </div></SplideSlide
        >
      {/each}
    </Splide>
  </div>
{/if}

<style lang="scss">
  @import '../../../../common/styles/colors.new.scss';

  .product-tile-index-container {
    :global {
      .splide.splide--draggable.splide--rtl {
        direction: unset;
      }
      .splide__arrow {
        background: $primary-black;
        border-radius: 50%;
        display: none;
        height: 48px;
        opacity: 0;
        width: 48px;
        svg {
          fill: $primary-white;
        }
      }
      .splide__arrow:disabled {
        opacity: 0;
        pointer-events: none;
      }
    }
    overflow-x: scroll;
    overflow-y: hidden;
    scrollbar-width: none; /* Also needed to disable scrollbar Firefox */
    &::-webkit-scrollbar {
      display: none;
    }
    .tile-index-wrapper {
      margin-right: 8px;
      height: 100%;
      @include desktop-min-width {
        margin-right: 24px;
      }
      .main-tile {
        display: flex;
        &.index-margin {
          margin-left: -35px;
          @include desktop-min-width {
            margin-left: -52px;
          }
          &.rtl {
            margin-left: -30px;
            @include desktop-min-width {
              margin-left: -30px;
            }
          }
        }

        :global {
          .tile-content-wrapper {
            display: flex;
            flex-direction: column;
            .product-tile-container {
              flex: 1;
            }
          }
        }
      }
    }

    .recomendation-viewed-link {
      text-decoration: none;
      color: unset;
      :global(.tile-content-wrapper) {
        height: 100%;
        @include responsive {
          width: 230px !important;
        }
      }
      :global(.product-tile-card-container) {
        @include desktop-max-width {
          max-width: 200px !important;
        }
      }
    }
  }
  .product-tile-index-container:hover {
    :global {
      .splide__arrow {
        display: inline-block;
        opacity: 0.7;
      }
      .splide__arrow:disabled {
        opacity: 0;
        pointer-events: none;
      }
    }
  }
</style>
